/**
 * Usability-test recorder (T11-50, design/04 §0 "ทดสอบกับคนจริง"): while the observer has switched
 * it on, the admin counts per task — time, clicks/taps, key presses, pauses longer than 5 s,
 * problems shown on screen (`role="alert"`), help opened, pages visited — and the observer
 * downloads one JSON file per session (`scripts/usability-summary.mjs` turns it into the report
 * table against the budgets).
 *
 * Nothing typed is kept (only the number of key presses), no customer data, no screenshots: the
 * screen recording of the session is made apart (docs/task/reports/T11-50-usability-script.md).
 */
export const USABILITY_TASKS = [
  { no: 1, key: "add_card", budgetSeconds: 120 },
  { no: 2, key: "confirm_transfer", budgetSeconds: 30 },
  { no: 3, key: "prepare_to_ship", budgetSeconds: 60 },
  { no: 4, key: "consignment_intake", budgetSeconds: 240 },
  { no: 5, key: "kobutsu_export", budgetSeconds: 30 },
] as const;

export type TaskKey = (typeof USABILITY_TASKS)[number]["key"];

/** A pause without any input longer than this counts as "hesitation" (> 5 s, T11-50). */
export const HESITATION_MS = 5_000;

export interface TaskRun {
  task: TaskKey;
  startedAt: string;
  finishedAt: string | null;
  seconds: number | null;
  budgetSeconds: number;
  withinBudget: boolean | null;
  clicks: number;
  keys: number;
  hesitations: Array<{ afterSeconds: number; pauseSeconds: number; path: string }>;
  problemsShown: number;
  helpOpened: number;
  pages: string[];
  gaveUp: boolean;
}

export interface Session {
  version: 1;
  device: { width: number; height: number; touch: boolean; language: string };
  startedAt: string;
  runs: TaskRun[];
}

type Clock = () => number;

/** The pure counter — the DOM listeners live in `UsabilityPanel.tsx`. */
export class UsabilityRecorder {
  readonly session: Session;
  private current: TaskRun | null = null;
  private startedMs = 0;
  private lastInputMs = 0;

  constructor(
    device: Session["device"],
    private readonly now: Clock = () => Date.now(),
  ) {
    this.session = { version: 1, device, startedAt: new Date(now()).toISOString(), runs: [] };
  }

  get running(): TaskKey | null {
    return this.current?.task ?? null;
  }

  start(task: TaskKey, path: string) {
    if (this.current) {
      this.finish(false);
    }
    const spec = USABILITY_TASKS.find((t) => t.key === task);
    const now = this.now();
    this.startedMs = now;
    this.lastInputMs = now;
    this.current = {
      task,
      startedAt: new Date(now).toISOString(),
      finishedAt: null,
      seconds: null,
      budgetSeconds: spec?.budgetSeconds ?? 0,
      withinBudget: null,
      clicks: 0,
      keys: 0,
      hesitations: [],
      problemsShown: 0,
      helpOpened: 0,
      pages: [path],
      gaveUp: false,
    };
  }

  private input(path: string) {
    const run = this.current;
    if (!run) {
      return;
    }
    const now = this.now();
    const pause = now - this.lastInputMs;
    if (pause > HESITATION_MS) {
      run.hesitations.push({
        afterSeconds: Math.round((this.lastInputMs - this.startedMs) / 100) / 10,
        pauseSeconds: Math.round(pause / 100) / 10,
        path,
      });
    }
    this.lastInputMs = now;
  }

  click(path: string, helpTip = false) {
    if (this.current) {
      this.input(path);
      this.current.clicks += 1;
      if (helpTip) {
        this.current.helpOpened += 1;
      }
    }
  }

  key(path: string) {
    if (this.current) {
      this.input(path);
      this.current.keys += 1;
    }
  }

  page(path: string) {
    const run = this.current;
    if (run && run.pages.at(-1) !== path) {
      run.pages.push(path);
      if (path.startsWith("/help")) {
        run.helpOpened += 1;
      }
    }
  }

  problem() {
    if (this.current) {
      this.current.problemsShown += 1;
    }
  }

  /** `done = false` — the person gave up (or the observer stopped the task). */
  finish(done = true) {
    const run = this.current;
    if (!run) {
      return null;
    }
    const now = this.now();
    run.finishedAt = new Date(now).toISOString();
    run.seconds = Math.round((now - this.startedMs) / 100) / 10;
    run.gaveUp = !done;
    run.withinBudget = done && run.seconds <= run.budgetSeconds;
    this.session.runs.push(run);
    this.current = null;
    return run;
  }

  toJson(): string {
    return `${JSON.stringify(this.session, null, 2)}\n`;
  }
}
